/**
 * useResolvedCycle
 *
 * Resolves the `:collectionId` route param against the shared collections list
 * for a cycle page, and guards that the collection exists, is loaded, and is a
 * PR Literature Review collection. The cycle page lives on
 * /pr-literature-review/cycle/:id, so this also catches a mismatched link (a
 * non-PR id on the cycle route) and treats it as not-found.
 *
 * While collections are still loading the hook reports `status: "loading"`.
 * When the id is missing/invalid, the collection is absent, or its type does
 * not match, it reports `status: "not-found"`; the page uses that to redirect
 * away.
 *
 * This is a dedicated copy of the journal articles `useResolvedCollection`,
 * wired to the PR Literature Review context and fixed to the PR cycle type.
 */

import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { usePrLiteratureReview } from "@/components/pr-literature-review/PrLiteratureReviewContext"
import { BiblioCollection, PR_LITERATURE_REVIEW_TYPE } from "@/services/biblioService"

/**
 * The outcome of resolving a cycle page's id param.
 */
export type ResolvedCycle =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "ready"; collection: BiblioCollection }

/**
 * Resolve the current route's collection id to a PR Literature Review cycle.
 *
 * @returns The resolution status and, when ready, the matched collection.
 */
export function useResolvedCycle(): ResolvedCycle {
  const { collectionId: collectionIdParam } = useParams<{ collectionId: string }>()
  const { collections, collectionsLoading } = usePrLiteratureReview()

  return useMemo((): ResolvedCycle => {
    // Still loading the collections list — cannot decide yet.
    if (collectionsLoading) {
      return { status: "loading" }
    }

    // Missing or non-numeric id.
    if (collectionIdParam === undefined) {
      return { status: "not-found" }
    }
    const parsedId: number = parseInt(collectionIdParam, 10)
    if (Number.isNaN(parsedId)) {
      return { status: "not-found" }
    }

    // No such collection, or not a PR Literature Review cycle.
    const match: BiblioCollection | undefined = collections.find((c) => c.id === parsedId)
    if (match === undefined || match.collectionType !== PR_LITERATURE_REVIEW_TYPE) {
      return { status: "not-found" }
    }

    return { status: "ready", collection: match }
  }, [collectionsLoading, collectionIdParam, collections])
}
